import { useMemo } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { getEntityById } from '@core/utils/collection';
import { useAppContext } from '../../../context/AppContext';
import { applyChasubleDesignPreset } from '../../../utils/designPreset';
import {
  type ChasubleItemFormValues,
  type OrderItemDialogSlotProps,
  type OrderItemFormValues,
  getFormValuesCategory,
  getVestmentsByCategory,
} from '../../../utils/orderItem';
import { BodySection } from './BodySection';
import { ChasubleItemSummary } from './ChasubleItemSummary';
import { CollarSection } from './CollarSection';
import { PanelSection } from './PanelSection';
import { useDesignPresetPrompt } from './useDesignPresetPrompt';

function ChasubleItemForm({
  methods: methodsProp,
  expandedSection,
  setExpandedSection,
  setPreviewTab,
}: OrderItemDialogSlotProps<OrderItemFormValues>) {
  const methods = methodsProp as UseFormReturn<ChasubleItemFormValues>;
  const { vestments, collars } = useAppContext();

  const vestmentId = methods.watch('vestment');
  const category = getFormValuesCategory(methods.getValues());
  const categoryVestments = useMemo(
    () => getVestmentsByCategory(vestments, category),
    [vestments, category]
  );
  const vestment = getEntityById(categoryVestments, vestmentId);
  const presetChooser = useDesignPresetPrompt(methods, (values) =>
    applyChasubleDesignPreset(methods, values, vestment, vestments, collars)
  );

  const sectionProps = (section: string) => ({
    expanded: expandedSection === section,
    onChange: (expanded: boolean) =>
      setExpandedSection(expanded ? section : null),
  });

  return (
    <>
      <BodySection
        {...sectionProps('body')}
        methods={methods}
        vestmentType={vestment?.type}
        setPreviewTab={setPreviewTab}
      />
      <PanelSection
        {...sectionProps('panel')}
        methods={methods}
        vestment={vestment}
      />
      <CollarSection
        {...sectionProps('collar')}
        methods={methods}
        vestment={vestment}
        setPreviewTab={setPreviewTab}
      />
      {presetChooser}
    </>
  );
}

const ChasubleItemDialogContent = {
  renderForm: (slot: OrderItemDialogSlotProps<OrderItemFormValues>) => (
    <ChasubleItemForm {...slot} />
  ),
  renderSummary: (slot: OrderItemDialogSlotProps<OrderItemFormValues>) => (
    <ChasubleItemSummary {...slot} />
  ),
};

export default ChasubleItemDialogContent;
